import React, { useState, useEffect } from 'react';
import { getSettings, updateSetting } from '../lib/database';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Save, DollarSign, Euro, Percent } from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      await Promise.all([
        updateSetting('usdToTl', parseFloat(settings.usdToTl)),
        updateSetting('eurToTl', parseFloat(settings.eurToTl)),
        updateSetting('defaultKdvRate', parseFloat(settings.defaultKdvRate)),
        updateSetting('defaultProfitMargin', parseFloat(settings.defaultProfitMargin)),
        updateSetting('defaultProfitType', settings.defaultProfitType)
      ]);
      
      setMessage('Ayarlar başarıyla kaydedildi!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Ayarlar kaydedilirken hata oluştu!');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>Ayarlar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Ayarlar</h1>
        <p className="text-muted-foreground">
          Döviz kurları ve varsayılan değerleri buradan düzenleyebilirsiniz.
        </p>
      </div>

      {message && (
        <Card className={message.includes('başarıyla') ? 'border-green-500' : 'border-red-500'}>
          <CardContent className="py-3">
            <p className={message.includes('başarıyla') ? 'text-green-600' : 'text-red-600'}>
              {message}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Currency Rates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="h-5 w-5" />
            <span>Döviz Kurları</span>
          </CardTitle>
          <CardDescription>
            Güncel döviz kurlarını manuel olarak girin. Bu değerler maliyet hesaplamalarında kullanılır.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="usdToTl">USD/TL Kuru</Label>
              <Input
                id="usdToTl"
                type="number"
                step="0.01"
                value={settings.usdToTl || ''}
                onChange={(e) => handleSettingChange('usdToTl', e.target.value)}
                placeholder="34.50"
              />
            </div>
            <div>
              <Label htmlFor="eurToTl">EUR/TL Kuru</Label>
              <Input
                id="eurToTl"
                type="number"
                step="0.01"
                value={settings.eurToTl || ''}
                onChange={(e) => handleSettingChange('eurToTl', e.target.value)}
                placeholder="37.20"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Default Values */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Percent className="h-5 w-5" />
            <span>Varsayılan Değerler</span>
          </CardTitle>
          <CardDescription>
            Yeni ürün eklerken kullanılacak varsayılan değerler.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="defaultKdvRate">Varsayılan KDV Oranı (%)</Label>
              <Input
                id="defaultKdvRate"
                type="number"
                step="0.1"
                value={settings.defaultKdvRate || ''}
                onChange={(e) => handleSettingChange('defaultKdvRate', e.target.value)}
                placeholder="20"
              />
            </div>
            <div>
              <Label htmlFor="defaultProfitMargin">Varsayılan Kar Marjı (%)</Label>
              <Input
                id="defaultProfitMargin"
                type="number"
                step="0.1"
                value={settings.defaultProfitMargin || ''}
                onChange={(e) => handleSettingChange('defaultProfitMargin', e.target.value)}
                placeholder="20"
              />
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded">
            <p><strong>Not:</strong> Bu ayarlar sadece yeni ürün eklerken kullanılır. Mevcut ürünlerin değerleri değişmez.</p>
          </div>
        </CardContent>
      </Card>

      {/* Sample Calculation */}
      <Card>
        <CardHeader>
          <CardTitle>Örnek Hesaplama</CardTitle>
          <CardDescription>
            Güncel ayarlarla örnek bir hesaplama
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm bg-muted/50 p-4 rounded">
            <div className="font-semibold">$10 + KDV ($2) maliyetli bir ürün için:</div>
            <div>• Net Maliyet: $10 (≈ {(10 * (settings.usdToTl || 0)).toFixed(2)} TL)</div>
            <div>• Satın Alma KDV: $2 (≈ {(2 * (settings.usdToTl || 0)).toFixed(2)} TL)</div>
            <div>• Toplam Satın Alma: $12 (≈ {(12 * (settings.usdToTl || 0)).toFixed(2)} TL)</div>
            <div>• Kar (%{settings.defaultProfitMargin || 0} net maliyetten): ${((10 * (settings.defaultProfitMargin || 0)) / 100).toFixed(2)} (≈ {((10 * (settings.defaultProfitMargin || 0) / 100) * (settings.usdToTl || 0)).toFixed(2)} TL)</div>
            <div>• Net Satış Fiyatı: ${(10 + (10 * (settings.defaultProfitMargin || 0) / 100)).toFixed(2)} (≈ {((10 + (10 * (settings.defaultProfitMargin || 0) / 100)) * (settings.usdToTl || 0)).toFixed(2)} TL)</div>
            <div>• Müşteri KDV (%{settings.defaultKdvRate || 0}): {(((10 + (10 * (settings.defaultProfitMargin || 0) / 100)) * (settings.defaultKdvRate || 0) / 100) * (settings.usdToTl || 0)).toFixed(2)} TL</div>
            <div className="font-semibold text-primary">
              • Brüt Satış Fiyatı: {(((10 + (10 * (settings.defaultProfitMargin || 0) / 100)) * (1 + (settings.defaultKdvRate || 0) / 100)) * (settings.usdToTl || 0)).toFixed(2)} TL
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="flex items-center space-x-2"
        >
          <Save className="h-4 w-4" />
          <span>{saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}</span>
        </Button>
      </div>
    </div>
  );
}